import type { Me, ServiceCode, ServiceMembership } from "./types";

/**
 * 前端的權限判斷工具。
 *
 * 注意：這些只用來決定「要不要顯示按鈕」。真正的授權由後端執行，
 * 前端的判斷純粹是 UX，不是安全邊界。
 *
 * 服務、角色與權限一律以 `/me/` 的回傳為準。前端**不維護**任何服務代碼
 * 或角色的常數清單——`ServiceCode` 這個型別是後端的 OpenAPI schema
 * 自動產生的，其內容由已安裝的服務 app 決定。
 */

export function serviceOf(
  me: Me | null | undefined,
  code: ServiceCode,
): ServiceMembership | undefined {
  return me?.services?.find((service) => service.code === code);
}

/** 使用者是否能進入某個服務 */
export function canAccessService(me: Me | null | undefined, code: ServiceCode): boolean {
  return Boolean(me?.is_superuser) || serviceOf(me, code) !== undefined;
}

/**
 * 使用者是否具備某個權限。
 *
 * @param permission 完整權限代碼，例如 "master.change_item"
 */
export function hasPermission(me: Me | null | undefined, permission: string): boolean {
  if (!me) return false;
  if (me.is_superuser) return true;

  const [serviceCode] = permission.split(".");
  if (!serviceCode) return false;

  return serviceOf(me, serviceCode as ServiceCode)?.permissions?.includes(permission) ?? false;
}

/** 全部具備才回傳 true */
export function hasAllPermissions(me: Me | null | undefined, permissions: string[]): boolean {
  return permissions.every((permission) => hasPermission(me, permission));
}

/** 具備任一個就回傳 true */
export function hasAnyPermission(me: Me | null | undefined, permissions: string[]): boolean {
  return permissions.some((permission) => hasPermission(me, permission));
}

/**
 * 使用者在某服務中是否具有指定角色。
 *
 * `roleCode` 刻意是 string 而不是列舉——角色是由 Admin 管理的資料，
 * 隨時可以新增或改名，不該在前端寫死成型別。
 */
export function hasRole(
  me: Me | null | undefined,
  serviceCode: ServiceCode,
  roleCode: string,
): boolean {
  return serviceOf(me, serviceCode)?.roles?.includes(roleCode) ?? false;
}
